import { useEffect, useMemo, useState } from "react";
import { getLineLeaderboard } from "@/lib/procureApi";
import { fmtDate, fmtInt } from "@/lib/format";
import { Trophy, TrendDown, TrendUp, Spinner } from "@phosphor-icons/react";

const Row = ({ r, tone }) => {
  const primary = tone === "reducer" ? r.cleared : r.new;
  const net = r.delta > 0 ? `+${r.delta}` : r.delta < 0 ? `−${Math.abs(r.delta)}` : "±0";
  return (
    <div
      data-testid={`ll-${tone}-${r.buyer}`}
      className="flex items-center justify-between gap-3 border-b border-[#E3E7EC]/60 py-2 last:border-0"
    >
      <span className="min-w-0 flex-1 truncate text-sm text-[#0F172A]" title={r.buyer}>{r.buyer}</span>
      <span className="shrink-0 font-mono text-[10px] text-[#94A3B8]">{fmtInt(r.base)}→{fmtInt(r.target)} · net {net}</span>
      <span className={`w-16 shrink-0 text-right font-mono text-sm font-semibold ${tone === "reducer" ? "text-emerald-600" : "text-[#F0323E]"}`}>
        {tone === "reducer" ? "↓" : "↑"}{fmtInt(primary)}
      </span>
    </div>
  );
};

const Empty = ({ text }) => <div className="py-6 text-center text-xs text-[#9CA3AF]">{text}</div>;

export const LineLeaderboard = ({ snapshots, refreshKey }) => {
  const list = snapshots || []; // descending: latest first
  const [mode, setMode] = useState("day");
  const [targetId, setTargetId] = useState("");
  const [baseId, setBaseId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!list.length) return;
    setTargetId((p) => (list.some((s) => s.id === p) ? p : list[0].id));
    setBaseId((p) => (list.some((s) => s.id === p) ? p : list[list.length - 1].id));
  }, [list]);

  // day mode: base = the snapshot chronologically before the target (next in descending list)
  const dayBaseId = useMemo(() => {
    const idx = list.findIndex((s) => s.id === targetId);
    return idx >= 0 && idx < list.length - 1 ? list[idx + 1].id : null;
  }, [list, targetId]);

  const effBase = mode === "day" ? dayBaseId : baseId;
  const effTarget = targetId;

  useEffect(() => {
    if (!effBase || !effTarget || effBase === effTarget) { setData(null); return; }
    setLoading(true);
    getLineLeaderboard(effBase, effTarget).then(setData).finally(() => setLoading(false));
  }, [effBase, effTarget, refreshKey]);

  const selCls = "h-9 rounded-sm border border-[#E3E7EC] bg-[#F1F3F6] px-2 text-sm text-[#0F172A] outline-none focus:border-[#F0323E]";

  return (
    <div className="col-span-12 border border-[#E3E7EC] bg-white p-5" data-testid="line-leaderboard">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Trophy size={18} weight="duotone" className="text-[#F0323E]" />
          <div>
            <h3 className="text-sm font-semibold text-[#0F172A]">Daily Leaderboard · Line Items Cleared</h3>
            <p className="pf-label mt-0.5">Reference 2 groups moved OQ → OF (cleared) vs newly assigned lines</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-sm border border-[#E3E7EC] p-0.5" data-testid="ll-mode-toggle">
            {[["day", "Single day"], ["range", "Range"]].map(([k, l]) => (
              <button
                key={k}
                data-testid={`ll-mode-${k}`}
                onClick={() => setMode(k)}
                className={`rounded-sm px-3 py-1 text-xs font-medium transition-colors ${mode === k ? "bg-[#F0323E] text-white" : "text-[#64748B] hover:text-[#0F172A]"}`}
              >
                {l}
              </button>
            ))}
          </div>
          {mode === "day" ? (
            <select data-testid="ll-day-select" value={targetId} onChange={(e) => setTargetId(e.target.value)} className={selCls}>
              {list.map((s, i) => (
                <option key={s.id} value={s.id}>{fmtDate(s.reference_date)}{i === 0 ? " · latest" : ""}</option>
              ))}
            </select>
          ) : (
            <div className="flex items-center gap-1.5">
              <select data-testid="ll-from-select" value={baseId} onChange={(e) => setBaseId(e.target.value)} className={selCls}>
                {list.map((s) => (<option key={s.id} value={s.id}>{fmtDate(s.reference_date)}</option>))}
              </select>
              <span className="text-xs text-[#94A3B8]">→</span>
              <select data-testid="ll-to-select" value={targetId} onChange={(e) => setTargetId(e.target.value)} className={selCls}>
                {list.map((s) => (<option key={s.id} value={s.id}>{fmtDate(s.reference_date)}</option>))}
              </select>
            </div>
          )}
        </div>
      </div>

      {data?.base_date && (
        <p className="pf-label mb-3">
          Comparing {fmtDate(data.base_date)} → {fmtDate(data.target_date)}
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-[#64748B]"><Spinner size={18} className="animate-spin" /> Loading…</div>
      ) : !effBase || effBase === effTarget ? (
        <Empty text={mode === "day" ? "No earlier snapshot to compare this day against." : "Pick two different dates to compare."} />
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <div className="mb-1 flex items-center gap-1.5">
              <TrendDown size={15} weight="bold" className="text-emerald-600" />
              <span className="pf-label text-emerald-600">Cleared the most (OQ → OF ↓)</span>
            </div>
            {data?.reducers?.length ? data.reducers.map((r) => <Row key={r.buyer} r={r} tone="reducer" />) : <Empty text="No quotes moved from OQ to OF." />}
          </div>
          <div>
            <div className="mb-1 flex items-center gap-1.5">
              <TrendUp size={15} weight="bold" className="text-[#F0323E]" />
              <span className="pf-label text-[#F0323E]">Most newly assigned (incoming ↑)</span>
            </div>
            {data?.risers?.length ? data.risers.map((r) => <Row key={r.buyer} r={r} tone="riser" />) : <Empty text="No buyer received new lines." />}
          </div>
        </div>
      )}
    </div>
  );
};
